import { apiClient } from '@/shared/api'
import { buildQueryString } from '@/shared/utils/buildQueryString'
import type { Result } from '@/shared/types'
import type { ApiError } from '@/shared/types/errors'
import type {
  GenerationRequest,
  GenerationResponse,
  GenerationStatusResponse,
  GenerationListResponse,
  SystemStatsResponse,
  ModelsListResponse,
  ResourceType,
  ImageUploadResponse,
  FollowUpRequest,
} from './types'

const BASE = '/api/generation'

export function submitGeneration(
  request: GenerationRequest,
): Promise<Result<GenerationResponse, ApiError>> {
  return apiClient.post<GenerationResponse>(BASE, request)
}

export function getGenerationStatus(
  id: string,
): Promise<Result<GenerationStatusResponse, ApiError>> {
  return apiClient.get<GenerationStatusResponse>(`${BASE}/${id}`)
}

export function cancelGeneration(
  id: string,
): Promise<Result<GenerationResponse, ApiError>> {
  return apiClient.post<GenerationResponse>(`${BASE}/${id}/cancel`)
}

export function listGenerations(
  limit = 20,
  offset = 0,
): Promise<Result<GenerationListResponse, ApiError>> {
  return apiClient.get<GenerationListResponse>(
    `${BASE}${buildQueryString({ limit, offset })}`,
  )
}

export function getSystemStats(): Promise<Result<SystemStatsResponse, ApiError>> {
  return apiClient.get<SystemStatsResponse>(`${BASE}/system/stats`)
}

export function getAvailableModels(
  resourceType: ResourceType,
): Promise<Result<ModelsListResponse, ApiError>> {
  return apiClient.get<ModelsListResponse>(`${BASE}/models/${resourceType}`)
}

export function uploadImage(
  file: File,
): Promise<Result<ImageUploadResponse, ApiError>> {
  const formData = new FormData()
  formData.append('file', file)
  return apiClient.postFormData<ImageUploadResponse>(`${BASE}/upload`, formData)
}

export function followUpGeneration(
  id: string,
  request: FollowUpRequest,
): Promise<Result<GenerationResponse, ApiError>> {
  return apiClient.post<GenerationResponse>(`${BASE}/${id}/follow-up`, request)
}

export function buildWsUrl(generationId: string): string {
  // In dev with Vite proxy, use relative path so Vite handles the upgrade
  // The browser's native WebSocket with a relative-like URL isn't supported,
  // so we build absolute URL using current origin.
  const wsBase = import.meta.env['VITE_WS_BASE_URL'] as string | undefined
  if (wsBase) {
    // Explicit WS base URL provided (e.g., ws://localhost:8001)
    return `${wsBase}${BASE}/ws/${generationId}`
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const host = window.location.host
  return `${protocol}//${host}${BASE}/ws/${generationId}`
}
